import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { MailPreferencesProvider } from './context/MailPreferencesContext';
import { UnreadInboxProvider } from './context/UnreadInboxContext';
import { MailFoldersProvider } from './context/MailFoldersContext';
import { MailDragProvider } from './context/MailDragContext';
import { PlannerProvider } from './context/PlannerContext';
import { PendingSendProvider, PendingSendBanner } from './context/PendingSendContext';
import Sidebar from './components/Sidebar';
import Dashboard from './components/Dashboard';
import Contacts from './components/Contacts';
import Templates from './components/Templates';
import Settings from './components/Settings';
import EmailInbox from './components/EmailInbox';
import EmailDetail from './components/EmailDetail';
import StickyNotesBoard from './components/StickyNotesBoard';
import StickyNotesOverlay from './components/StickyNotesOverlay';
import MailDropTray from './components/MailDropTray';
import CalendarPage from './components/CalendarPage';
import MediaLibraryPage from './components/MediaLibraryPage';
import AiChat from './components/AiChat';
import Login from './components/Login';
import PublicUnsubscribe from './components/PublicUnsubscribe';
import InboxDesktopMailWatcher from './components/InboxDesktopMailWatcher';
import DesktopNotificationBootstrap from './components/DesktopNotificationBootstrap';
import UnreadTabIndicator from './components/UnreadTabIndicator';
import CalendarReminderWatcher from './components/CalendarReminderWatcher';
import AppToaster from './components/AppToaster';
import { Menu } from 'lucide-react';
import './App.css';

const PageShell = ({ children }) => (
  <div className="h-full overflow-auto">
    <div className="container mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8 max-w-7xl min-h-full">
      {children}
    </div>
  </div>
);

/** Volle Inhaltsbreite — wie Kalender (kein max-w-7xl) */
const FullPageShell = ({ children }) => (
  <div className="h-full overflow-auto">
    <div className="w-full max-w-none px-3 sm:px-4 lg:px-5 py-3 sm:py-4 lg:py-5 min-h-full">
      {children}
    </div>
  </div>
);

/** Hauptseiten: nach erstem Besuch im DOM behalten → kein erneutes Skeleton */
const KEEP_ALIVE_PAGES = [
  {
    key: 'dashboard',
    match: (p) => p === '/',
    fill: true,
    render: () => (
      <div className="h-full min-h-0 overflow-hidden">
        <Dashboard />
      </div>
    ),
  },
  {
    key: 'inbox',
    match: (p) => p === '/inbox',
    fill: true,
    render: () => <EmailInbox />,
  },
  {
    key: 'contacts',
    match: (p) => p === '/contacts',
    fill: false,
    render: () => (
      <FullPageShell>
        <Contacts />
      </FullPageShell>
    ),
  },
  {
    key: 'templates',
    match: (p) => p === '/templates',
    fill: false,
    render: () => (
      <FullPageShell>
        <Templates />
      </FullPageShell>
    ),
  },
  {
    key: 'sticky-notes',
    match: (p) => p === '/sticky-notes',
    fill: true,
    render: () => (
      <div className="h-full min-h-0 overflow-hidden">
        <StickyNotesBoard />
      </div>
    ),
  },
  {
    key: 'calendar',
    match: (p) => p === '/calendar',
    fill: true,
    render: () => (
      <div className="h-full min-h-0 overflow-hidden">
        <CalendarPage />
      </div>
    ),
  },
  {
    key: 'media',
    match: (p) => p === '/media',
    fill: false,
    render: () => (
      <FullPageShell>
        <MediaLibraryPage />
      </FullPageShell>
    ),
  },
  {
    key: 'ai-chat',
    match: (p) => p === '/ai-chat',
    fill: true,
    render: () => (
      <div className="h-full min-h-0 overflow-hidden">
        <AiChat />
      </div>
    ),
  },
  {
    key: 'settings',
    match: (p) => p === '/settings',
    fill: false,
    render: () => (
      <PageShell>
        <Settings />
      </PageShell>
    ),
  },
];

const resolveKeepAliveKey = (pathname) => {
  const page = KEEP_ALIVE_PAGES.find((p) => p.match(pathname));
  return page?.key || null;
};

const KeepAlivePages = () => {
  const location = useLocation();
  const pathname = location.pathname;
  const activeKey = resolveKeepAliveKey(pathname);
  const isEmailDetail = /^\/inbox\/email\//.test(pathname);

  const [mounted, setMounted] = useState(() => {
    const key = resolveKeepAliveKey(pathname);
    return key ? new Set([key]) : new Set();
  });

  useEffect(() => {
    const key = resolveKeepAliveKey(pathname);
    if (!key) return;
    setMounted((prev) => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  }, [pathname]);

  return (
    <>
      {KEEP_ALIVE_PAGES.map((page) => {
        if (!mounted.has(page.key)) return null;
        const active = activeKey === page.key;
        return (
          <div
            key={page.key}
            className={`${page.fill ? 'h-full min-h-0' : 'h-full'} ${active ? '' : 'hidden'}`}
            aria-hidden={!active}
          >
            {page.render()}
          </div>
        );
      })}
      {pathname === '/onlinesearch' ||
      pathname === '/company-finder' ||
      pathname === '/campaigns' ? (
        <Navigate to="/" replace />
      ) : null}
      {!activeKey &&
      !isEmailDetail &&
      pathname !== '/onlinesearch' &&
      pathname !== '/company-finder' &&
      pathname !== '/campaigns' ? (
        <Navigate to="/" replace />
      ) : null}
    </>
  );
};

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 dark:bg-neutral-950">
        <div className="spinner" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return children;
};

const AppShell = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const hideMobileTopBar =
    location.pathname === '/ai-chat' ||
    location.pathname === '/inbox' ||
    /^\/inbox\/email\//.test(location.pathname);

  useEffect(() => {
    const open = () => setSidebarOpen(true);
    window.addEventListener('odium-open-sidebar', open);
    return () => window.removeEventListener('odium-open-sidebar', open);
  }, []);

  return (
    <UnreadInboxProvider>
      <UnreadTabIndicator />
      <DesktopNotificationBootstrap />
      <InboxDesktopMailWatcher />
      <MailFoldersProvider>
      <MailDragProvider>
      <PlannerProvider>
      <CalendarReminderWatcher />
      <PendingSendProvider>
      <div className="flex h-screen h-[100dvh] overflow-hidden bg-stone-50 dark:bg-neutral-950">
        <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

        <div className="flex-1 flex flex-col overflow-hidden min-w-0">
          {!hideMobileTopBar ? (
            <div className="lg:hidden flex items-center justify-between bg-white dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-800 px-3 py-2.5 flex-shrink-0">
              <button
                onClick={() => setSidebarOpen(true)}
                className="p-2.5 -ml-1 rounded-lg text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors touch-manipulation"
                aria-label="Menü öffnen"
              >
                <Menu className="w-5 h-5" />
              </button>
              <span className="text-sm font-semibold text-neutral-900 dark:text-white">Odium Manager</span>
              <div className="w-10" />
            </div>
          ) : null}

          <main className="flex-1 overflow-hidden min-h-0">
            <KeepAlivePages />
            <Routes>
              <Route
                path="/inbox/email/:uid"
                element={(
                  <PageShell>
                    <EmailDetail />
                  </PageShell>
                )}
              />
            </Routes>
          </main>
        </div>
      </div>
      <StickyNotesOverlay />
      <MailDropTray />
      <PendingSendBanner />
      </PendingSendProvider>
      </PlannerProvider>
      </MailDragProvider>
      </MailFoldersProvider>
    </UnreadInboxProvider>
  );
};

function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<Login />} />
            {/* Öffentliche Abmeldung — niemals hinter Login */}
            <Route path="/bauos-Newsletter" element={<PublicUnsubscribe />} />
            <Route path="/bauos-newsletter" element={<PublicUnsubscribe />} />
            <Route
              path="/*"
              element={(
                <ProtectedRoute>
                  <MailPreferencesProvider>
                    <AppShell />
                  </MailPreferencesProvider>
                </ProtectedRoute>
              )}
            />
          </Routes>

          <AppToaster />
        </Router>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
